import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { BarcodeLookupResponse } from "../../shared/api/requests";
import { CategoryChip } from "../../shared/components/CategoryChip";
import { BarcodeIcon } from "../../shared/components/icons";
import { useT } from "../../shared/i18n";

/** Why a product lookup could not produce a provider preview (results-02). */
export type ProductLookupFailure = "miss" | "offline" | "error";

/** The provider step of an unknown product barcode on the global surface. */
export type ProductPhase =
  | { kind: "looking" }
  | { kind: "preview"; preview: BarcodeLookupResponse }
  | { kind: "manual"; reason: ProductLookupFailure };

/**
 * The unknown-product panel: the provider preview (name, category, photo,
 * metadata) or the reason the lookup found nothing, always naming the scanned
 * barcode. Adding is the CTA bar's job, so this panel carries no create button
 * of its own. It never offers a container: the created item is unplaced and
 * placing stays the container-add loop's job.
 */
export function ProductPreview({
  code,
  phase,
  error,
}: {
  code: string;
  phase: ProductPhase;
  error: string | null;
}) {
  const t = useT();

  if (phase.kind === "looking") {
    return (
      <Box data-testid="scan-product-looking">
        <LinearProgress sx={{ my: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {t("scanProductLooking")}
        </Typography>
      </Box>
    );
  }

  if (phase.kind === "manual") {
    const reason =
      phase.reason === "miss"
        ? t("scanProductMiss", { barcode: code })
        : phase.reason === "offline"
          ? t("scanProductOffline")
          : t("scanProductError");
    return (
      <Box data-testid="scan-product-manual">
        <Typography variant="body2" color="text.secondary" data-testid="scan-product-reason">
          {reason}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          {code}
        </Typography>
        {error && (
          <Typography variant="body2" color="error" data-testid="scan-product-error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </Box>
    );
  }

  const { preview } = phase;
  const metadata = Object.entries(preview.metadata);

  return (
    <Box data-testid="scan-product-summary">
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        {preview.photo ? (
          <Box
            component="img"
            data-testid="scan-product-photo"
            src={preview.photo}
            alt=""
            sx={{ width: 96, height: 128, objectFit: "contain", borderRadius: 1, flexShrink: 0 }}
          />
        ) : (
          <Box
            data-testid="scan-product-photo-placeholder"
            sx={{
              width: 96,
              height: 128,
              borderRadius: 1,
              bgcolor: "action.hover",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <BarcodeIcon fontSize="large" color="disabled" />
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h6"
            data-testid="scan-product-name"
            sx={{ lineHeight: 1.2, wordBreak: "break-word" }}
          >
            {preview.name}
          </Typography>
          <Box data-testid="scan-product-category" sx={{ mt: 0.5 }}>
            <CategoryChip category={preview.category} />
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5, wordBreak: "break-all" }}
          >
            {code}
          </Typography>
        </Box>
      </Stack>

      {metadata.length > 0 && (
        <Box data-testid="scan-product-metadata" sx={{ mt: 1 }}>
          {metadata.map(([key, value]) => (
            <Typography key={key} variant="body2" color="text.secondary">
              {key}: {formatMetadataValue(value)}
            </Typography>
          ))}
        </Box>
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        data-testid="scan-product-hint"
        sx={{ display: "block", mt: 1 }}
      >
        {t("scanProductHint")}
      </Typography>

      {error && (
        <Typography variant="body2" color="error" data-testid="scan-product-error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}

/** Provider metadata values are display-only: stringify objects, never render HTML. */
function formatMetadataValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  return typeof value === "object" ? JSON.stringify(value) : String(value);
}
